import { useState, useEffect, useCallback } from "react";
import { Newspaper, RefreshCw, ExternalLink, Search } from "lucide-react";
import api from "../lib/api";
import { clsx } from "../lib/format";
import { Input } from "../components/ui/input";

const ago = (ts) => {
  const s = Math.max(1, Math.floor(Date.now() / 1000 - ts));
  if (s < 3600) return `${Math.floor(s / 60) || 1}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function NewsCard({ n, featured }) {
  return (
    <a href={n.link} target="_blank" rel="noopener noreferrer" data-testid={`news-item-${n.source.toLowerCase()}`}
      className={clsx("group rounded-xl bg-fx-s1 border border-white/5 overflow-hidden flex flex-col hover:border-brandblue/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-200", featured && "md:col-span-2 md:flex-row")}>
      {n.image && (
        <div className={clsx("bg-fx-base overflow-hidden shrink-0", featured ? "md:w-1/2 aspect-video md:aspect-auto" : "aspect-video")}>
          <img src={n.image} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
        </div>
      )}
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-center gap-2 text-[11px] font-mono">
          <span className="text-brandblue font-bold uppercase">{n.source}</span>
          <span className="text-fx-s3">· {ago(n.published_at)}</span>
        </div>
        <h3 className={clsx("font-display font-bold leading-snug group-hover:text-brandblue transition-colors", featured ? "text-xl" : "text-base")}>{n.title}</h3>
        {n.summary && <p className="text-sm text-fx-s3 line-clamp-3">{n.summary}</p>}
        <div className="mt-auto pt-2 flex items-center justify-between">
          <div className="flex gap-1 flex-wrap">{n.categories.slice(0, 2).map((c) => <span key={c} className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-fx-s3">{c}</span>)}</div>
          <ExternalLink className="h-3.5 w-3.5 text-fx-s3 group-hover:text-brandblue" />
        </div>
      </div>
    </a>
  );
}

export default function News() {
  const [data, setData] = useState({ items: [], sources: [] });
  const [source, setSource] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/news").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 300000); return () => clearInterval(t); }, [load]);

  const items = data.items.filter((n) => (source === "All" || n.source === source) && n.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-[1400px]" data-testid="news-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2"><Newspaper className="h-6 w-6 text-brandblue" /><h1 className="font-display text-3xl font-extrabold tracking-tight">Crypto News</h1></div>
          <p className="text-sm text-fx-s3 mt-1">Live headlines from CoinDesk, Cointelegraph and Decrypt.</p>
        </div>
        <button onClick={load} data-testid="news-refresh-btn" className="flex items-center gap-2 text-sm text-fx-s3 hover:text-white px-3 py-2 rounded-lg border border-white/10">
          <RefreshCw className={clsx("h-4 w-4", loading && "animate-spin")} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {["All", ...data.sources].map((s) => (
          <button key={s} onClick={() => setSource(s)} data-testid={`news-filter-${s.toLowerCase()}`}
            className={clsx("px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors", source === s ? "bg-brandblue border-brandblue text-white" : "border-white/10 text-fx-s3 hover:text-white")}>{s}</button>
        ))}
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-fx-s3" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search headlines" data-testid="news-search-input" className="pl-9 bg-fx-s1 border-white/10" />
        </div>
      </div>

      {loading && !data.items.length ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-72 rounded-xl bg-fx-s1 animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <div className="text-center text-fx-s3 py-16" data-testid="news-empty">No news found.</div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="news-list">
          {items.map((n, i) => <NewsCard key={n.id} n={n} featured={i === 0 && !q} />)}
        </div>
      )}
    </div>
  );
}
